import React, { useCallback, useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import axios from 'axios';
import { Translate, I18n } from 'react-redux-i18n';
import { config } from 'config';
import { pickLegalHtml } from 'utils/legalContent';
import MiniLoader from 'components/MiniLoader';
import 'assets/css/signin.css';

const ENDPOINTS = {
    tos: 'legal/tos/public',
    pop: 'legal/pop/public',
};

const LegalDocumentModal = ({
    kind,
    titleKey,
    buttonClassName,
    buttonLabelKey,
}) => {
    const [open, setOpen] = useState(false);
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [html, setHtml] = useState('');

    const handleClose = useCallback(() => setOpen(false), []);

    const loadContent = useCallback(async () => {
        setLoading(true);
        setError('');
        try {
            const response = await axios.get(`${config.api.web}${ENDPOINTS[kind]}`);
            if (response.data?.status !== 'Success') {
                throw new Error('load_failed');
            }

            const item = response.data.body?.list?.[0];
            const content = pickLegalHtml(item, I18n.locale);
            if (!content) {
                throw new Error('empty_content');
            }
            setHtml(content);
        } catch (err) {
            setHtml('');
            setError(I18n.t('legal_load_failed'));
        } finally {
            setLoading(false);
        }
    }, [kind]);

    const handleOpen = async () => {
        setOpen(true);
        if (!html) {
            await loadContent();
        }
    };

    useEffect(() => {
        if (!open) {
            return undefined;
        }

        const onKeyDown = (event) => {
            if (event.key === 'Escape') {
                handleClose();
            }
        };

        document.body.style.overflow = 'hidden';
        document.addEventListener('keydown', onKeyDown);

        return () => {
            document.body.style.overflow = '';
            document.removeEventListener('keydown', onKeyDown);
        };
    }, [open, handleClose]);

    return (
        <>
            <button
                type="button"
                className={buttonClassName}
                onClick={handleOpen}
            >
                <Translate value={buttonLabelKey || titleKey} />
            </button>

            {open && (
                <div
                    className="tos-modal-overlay"
                    role="presentation"
                    onClick={handleClose}
                >
                    <div
                        className="tos-modal"
                        role="dialog"
                        aria-modal="true"
                        aria-labelledby={`legal-modal-title-${kind}`}
                        onClick={(event) => event.stopPropagation()}
                    >
                        <div className="tos-modal-header">
                            <h2 id={`legal-modal-title-${kind}`} className="tos-modal-title">
                                <Translate value={titleKey} />
                            </h2>
                            <button
                                type="button"
                                className="tos-modal-close"
                                onClick={handleClose}
                                aria-label={I18n.t('close')}
                            >
                                ×
                            </button>
                        </div>

                        <div className="tos-modal-body tos-modal-body--html">
                            {loading && (
                                <div className="legal-modal-loading">
                                    <MiniLoader />
                                </div>
                            )}
                            {!loading && error && (
                                <p className="legal-modal-error">{error}</p>
                            )}
                            {!loading && !error && html && (
                                <div dangerouslySetInnerHTML={{ __html: html }} />
                            )}
                        </div>

                        <div className="tos-modal-footer">
                            <button
                                type="button"
                                className="btn btn-primary"
                                onClick={handleClose}
                            >
                                <Translate value="close" />
                            </button>
                        </div>
                    </div>
                </div>
            )}
        </>
    );
};

LegalDocumentModal.propTypes = {
    kind: PropTypes.oneOf(['tos', 'pop']).isRequired,
    titleKey: PropTypes.string.isRequired,
    buttonClassName: PropTypes.string,
    buttonLabelKey: PropTypes.string,
};

LegalDocumentModal.defaultProps = {
    buttonClassName: 'btn btn-text tos-link',
};

export default LegalDocumentModal;
